import React from 'react';
import { Link as MuiLink } from '@mui/material';
import { Link } from 'react-router-dom';
import { useLocalizedPath } from '../hooks/useLocalizedPath';

/**
 * A deliberately tiny inline formatting layer for CMS-authored text.
 *
 * Editors get the three things they actually reach for - bold, italic and
 * links - written the way they already know from messaging apps:
 *
 *   **fett**   *kursiv*   [Text](/organisation)   [Text](https://example.org)
 *
 * Everything else is rendered literally. There is no HTML path here at all:
 * the parser emits React elements, so a pasted `<script>` is text and nothing
 * more. That is the whole reason this exists instead of a markdown library
 * plus dangerouslySetInnerHTML.
 */

/*
 * Ordered by precedence: links, then bold, then italic.
 *
 * The URL part tolerates one level of nested parentheses, because plenty of
 * the sources this organisation links to have them — Wikipedia articles like
 * .../Myalgic_encephalomyelitis_(disease) are the everyday case.
 */
const URL_SOURCE = '[^()\\s]*(?:\\([^()\\s]*\\)[^()\\s]*)*';
const INLINE_PATTERN = new RegExp(
  `\\[([^\\]\\n]+)\\]\\((${URL_SOURCE})\\)|\\*\\*([^*\\n]+)\\*\\*|\\*([^*\\n]+)\\*`,
  'g'
);

/** Schemes an editor may legitimately link to. Anything else is not a link. */
const SAFE_SCHEME = /^(https?:|mailto:|tel:)/i;

const isInternal = (url: string) => url.startsWith('/') || url.startsWith('#');

export const isSafeUrl = (url: string): boolean => isInternal(url) || SAFE_SCHEME.test(url);

/** Renders one link, routed internally or opened externally as appropriate. */
const InlineLink: React.FC<{ url: string; children: React.ReactNode }> = ({ url, children }) => {
  const lp = useLocalizedPath();

  // In-page anchors must not be language-prefixed - they never leave the page.
  if (url.startsWith('#')) {
    return <MuiLink href={url}>{children}</MuiLink>;
  }

  if (url.startsWith('/')) {
    return (
      <MuiLink component={Link} to={lp(url)}>
        {children}
      </MuiLink>
    );
  }

  return (
    <MuiLink href={url} target="_blank" rel="noopener noreferrer">
      {children}
    </MuiLink>
  );
};

const parse = (text: string, keyPrefix: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // A fresh regex per call - lastIndex on a shared instance is a trap.
  const pattern = new RegExp(INLINE_PATTERN.source, 'g');

  // eslint-disable-next-line no-cond-assign
  while ((match = pattern.exec(text)) !== null) {
    const [full, linkLabel, linkUrl, bold, italic] = match;

    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    lastIndex = match.index + full.length;

    const key = `${keyPrefix}-${match.index}`;

    if (linkLabel !== undefined && linkUrl !== undefined) {
      if (isSafeUrl(linkUrl)) {
        nodes.push(
          <InlineLink key={key} url={linkUrl}>
            {parse(linkLabel, key)}
          </InlineLink>
        );
      } else {
        // Not a scheme we will link to. The raw text is kept exactly as it was
        // typed, rather than quietly turning into plain words: an editor who
        // sees their own brackets on the page knows something needs fixing.
        nodes.push(full);
      }
    } else if (bold !== undefined) {
      nodes.push(<strong key={key}>{parse(bold, key)}</strong>);
    } else if (italic !== undefined) {
      nodes.push(<em key={key}>{parse(italic, key)}</em>);
    }
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
};

/** Inline-formats a single string. Block layout stays with the caller. */
export const RichText: React.FC<{ text: string }> = ({ text }) => <>{parse(text, 'rt')}</>;
